import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CHEF_PROFILE_COLORS } from '../ticketConstants';
import { newProfileId } from '../ticketUtils';
import { useSquareCatalog } from '../hooks/useSquareCatalog';

export function ChefProfileManager({ profiles, activeId, onSelect, onClose, onChange }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', color: CHEF_PROFILE_COLORS[0], keywords: [], categoryColors: {} });
  const [pickerTab, setPickerTab] = useState('categories');
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState('');
  const [customInput, setCustomInput] = useState('');

  const { catalog, loading: catLoading } = useSquareCatalog();

  const openEdit = (p) => {
    setEditing(p.id); setCreating(false);
    setForm({ name: p.name, color: p.color, keywords: [...p.keywords], categoryColors: p.categoryColors || {} });
    setPickerTab('categories'); setActiveCat(null); setSearch(''); setCustomInput('');
  };
  const openCreate = () => {
    setEditing(null); setCreating(true);
    setForm({ name: '', color: CHEF_PROFILE_COLORS[profiles.length % CHEF_PROFILE_COLORS.length], keywords: [], categoryColors: {} });
    setPickerTab('categories'); setActiveCat(null); setSearch(''); setCustomInput('');
  };

  const toggleKw = (kw) => setForm(f => ({
    ...f,
    keywords: f.keywords.includes(kw) ? f.keywords.filter(k => k !== kw) : [...f.keywords, kw],
  }));

  const addCustom = () => {
    const kw = customInput.trim();
    if (!kw || form.keywords.includes(kw)) return;
    setForm(f => ({ ...f, keywords: [...f.keywords, kw] }));
    setCustomInput('');
  };

  const save = () => {
    if (!form.name.trim()) return;
    let next;
    if (creating) {
      next = [...profiles, { id: newProfileId(), name: form.name.trim(), color: form.color, keywords: form.keywords, categoryColors: form.categoryColors || {} }];
    } else {
      next = profiles.map(p => p.id === editing
        ? { ...p, name: form.name.trim(), color: form.color, keywords: form.keywords, categoryColors: form.categoryColors || {} } : p);
    }
    onChange(next);
    setEditing(null); setCreating(false);
  };

  const deleteProfile = (id) => {
    if (!window.confirm('Delete this profile?')) return;
    const next = profiles.filter(p => p.id !== id);
    onChange(next);
    if (activeId === id) onSelect(null);
    if (editing === id) { setEditing(null); setCreating(false); }
  };

  const isFormOpen = creating || editing != null;
  const catNames = new Set(catalog.map(c => c.name));
  const selectedCats = form.keywords.filter(kw => catNames.has(kw));
  const selectedItems = form.keywords.filter(kw => !catNames.has(kw));

  const q = search.toLowerCase();
  const filteredCats = catalog.filter(c => !q || c.name.toLowerCase().includes(q));
  const drillItems = activeCat
    ? (catalog.find(c => c.name === activeCat)?.items || []).filter(n => !q || n.toLowerCase().includes(q))
    : [];
  const allItemsFlat = catalog.flatMap(c => c.items.map(n => ({ name: n, cat: c.name })));
  const filteredItems = allItemsFlat.filter(i => !q || i.name.toLowerCase().includes(q) || i.cat.toLowerCase().includes(q));

  return createPortal(
    <div className="cpm__overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cpm__panel">
        <div className="cpm__hd">
          <span className="cpm__title">👨‍🍳 Chef Profiles</span>
          <button className="cpm__close" onClick={onClose}>✕</button>
        </div>

        {/* Profile list */}
        {!isFormOpen && (
          <div className="cpm__list">
            <div className={`cpm__profile-row${!activeId ? ' cpm__profile-row--active' : ''}`} onClick={() => onSelect(null)}>
              <span className="cpm__profile-dot" style={{ background: '#475569' }} />
              <span className="cpm__profile-name">None (show all items)</span>
              {!activeId && <span className="cpm__check">✓</span>}
            </div>
            {profiles.map(p => (
              <div key={p.id} className={`cpm__profile-row${activeId === p.id ? ' cpm__profile-row--active' : ''}`}>
                <span className="cpm__profile-dot" style={{ background: p.color }} onClick={() => onSelect(p.id)} />
                <span className="cpm__profile-name" onClick={() => onSelect(p.id)}>{p.name}</span>
                <span className="cpm__profile-kws">{p.keywords.length} selection{p.keywords.length !== 1 ? 's' : ''}</span>
                {activeId === p.id && <span className="cpm__check">✓</span>}
                <button className="cpm__edit-btn" onClick={() => openEdit(p)} title="Edit">✏️</button>
                <button className="cpm__del-btn" onClick={() => deleteProfile(p.id)} title="Delete">🗑</button>
              </div>
            ))}
            {profiles.length === 0 && <p className="cpm__empty">No profiles yet. Create one to highlight your items.</p>}
          </div>
        )}

        {/* Create / Edit form */}
        {isFormOpen && (
          <div className="cpm__form">
            <div className="cpm__form-hd">{creating ? '+ New Profile' : 'Edit Profile'}</div>

            <label className="cpm__lbl">Profile name</label>
            <input className="cpm__input" placeholder="e.g. Dosa Station, Wok Corner…"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />

            <label className="cpm__lbl">Highlight colour</label>
            <div className="cpm__colors">
              {CHEF_PROFILE_COLORS.map(c => (
                <button key={c} className={`cpm__color-swatch${form.color === c ? ' cpm__color-swatch--on' : ''}`}
                  style={{ background: c }} onClick={() => setForm(f => ({ ...f, color: c }))} />
              ))}
            </div>

            {/* Selected summary */}
            {form.keywords.length > 0 && (
              <div className="cpm__selected-wrap">
                <div className="cpm__selected-label">
                  Selected — {selectedCats.length > 0 && `${selectedCats.length} categor${selectedCats.length !== 1 ? 'ies' : 'y'}`}
                  {selectedCats.length > 0 && selectedItems.length > 0 && ' + '}
                  {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''}`}
                </div>
                <div className="cpm__selected-chips">
                  {form.keywords.map(kw => (
                    <span key={kw} className={`cpm__sel-chip${catNames.has(kw) ? ' cpm__sel-chip--cat' : ''}`}
                      style={{ borderColor: form.color, color: form.color }}>
                      {catNames.has(kw) ? '📂 ' : ''}{kw}
                      <button className="cpm__kw-chip-del" onClick={() => toggleKw(kw)}>✕</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Per-category colour overrides */}
            {selectedCats.length > 0 && (
              <div className="cpm__cat-colors-section">
                <div className="cpm__lbl">Category colours <span className="cpm__lbl-hint">(override profile colour per category)</span></div>
                {selectedCats.map(catName => {
                  const activeCatColor = form.categoryColors[catName] || form.color;
                  return (
                    <div key={catName} className="cpm__cat-color-row">
                      <span className="cpm__cat-color-label">
                        <span className="cpm__cat-color-preview" style={{ background: activeCatColor }} />
                        {catName}
                      </span>
                      <div className="cpm__colors cpm__colors--sm">
                        {CHEF_PROFILE_COLORS.map(c => (
                          <button key={c}
                            className={`cpm__color-swatch cpm__color-swatch--sm${activeCatColor === c ? ' cpm__color-swatch--on' : ''}`}
                            style={{ background: c }} title={c}
                            onClick={() => setForm(f => ({ ...f, categoryColors: { ...f.categoryColors, [catName]: c } }))} />
                        ))}
                        {form.categoryColors[catName] && (
                          <button className="cpm__cat-color-reset" title="Reset to profile colour"
                            onClick={() => setForm(f => {
                              const cc = { ...f.categoryColors }; delete cc[catName]; return { ...f, categoryColors: cc };
                            })}>↩</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Picker tabs */}
            <div className="cpm__picker-tabs">
              <button className={`cpm__picker-tab${pickerTab === 'categories' ? ' cpm__picker-tab--on' : ''}`}
                onClick={() => { setPickerTab('categories'); setActiveCat(null); setSearch(''); }}>📂 By Category</button>
              <button className={`cpm__picker-tab${pickerTab === 'items' ? ' cpm__picker-tab--on' : ''}`}
                onClick={() => { setPickerTab('items'); setActiveCat(null); setSearch(''); }}>🍽 By Item</button>
            </div>

            <input className="cpm__input cpm__search-input"
              placeholder={pickerTab === 'categories' ? '🔍 Search categories…' : '🔍 Search items…'}
              value={search} onChange={e => { setSearch(e.target.value); setActiveCat(null); }} />

            {catLoading ? (
              <p className="cpm__empty" style={{ padding: '.6rem 0' }}>Loading Square catalog…</p>
            ) : catalog.length === 0 ? (
              <p className="cpm__empty">Could not load catalog — use custom keywords below.</p>
            ) : (
              <>
                {pickerTab === 'categories' && !activeCat && (
                  <div className="cpm__cat-grid">
                    {filteredCats.length === 0
                      ? <p className="cpm__empty" style={{ padding: '.4rem 0' }}>No categories match</p>
                      : filteredCats.map(cat => {
                          const on = form.keywords.includes(cat.name);
                          return (
                            <div key={cat.name} className="cpm__cat-row">
                              <button className={`cpm__cat-chip${on ? ' cpm__cat-chip--on' : ''}`}
                                style={on ? { borderColor: form.color, background: form.color + '33', color: '#fff' } : undefined}
                                onClick={() => toggleKw(cat.name)}>
                                {on && '✓ '}{cat.name}
                                <span className="cpm__cat-count">{cat.items.length}</span>
                              </button>
                              <button className="cpm__cat-drill" onClick={() => { setActiveCat(cat.name); setSearch(''); }}
                                title={`See items in ${cat.name}`}>›</button>
                            </div>
                          );
                        })
                    }
                  </div>
                )}

                {pickerTab === 'categories' && activeCat && (
                  <div className="cpm__drill">
                    <button className="cpm__drill-back" onClick={() => { setActiveCat(null); setSearch(''); }}>← {activeCat}</button>
                    <div className="cpm__menu-grid">
                      {drillItems.map(name => {
                        const on = form.keywords.includes(name);
                        return (
                          <button key={name} className={`cpm__menu-chip${on ? ' cpm__menu-chip--on' : ''}`}
                            style={on ? { borderColor: form.color, background: form.color + '22', color: '#fff' } : undefined}
                            onClick={() => toggleKw(name)}>
                            {on && <span className="cpm__menu-chip-check">✓ </span>}{name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {pickerTab === 'items' && (
                  <div className="cpm__menu-grid">
                    {filteredItems.length === 0
                      ? <p className="cpm__empty" style={{ padding: '.4rem 0' }}>No items match</p>
                      : filteredItems.map(({ name, cat }) => {
                          const on = form.keywords.includes(name);
                          return (
                            <button key={`${cat}::${name}`} className={`cpm__menu-chip${on ? ' cpm__menu-chip--on' : ''}`}
                              style={on ? { borderColor: form.color, background: form.color + '22', color: '#fff' } : undefined}
                              onClick={() => toggleKw(name)} title={cat}>
                              {on && <span className="cpm__menu-chip-check">✓ </span>}{name}
                            </button>
                          );
                        })
                    }
                  </div>
                )}
              </>
            )}

            {/* Custom keyword fallback */}
            <details className="cpm__custom-wrap">
              <summary className="cpm__custom-toggle">+ Add custom keyword (text match)</summary>
              <div className="cpm__kw-add">
                <input className="cpm__input cpm__kw-input" placeholder="e.g. catering, special…"
                  value={customInput} onChange={e => setCustomInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCustom()} />
                <button className="cpm__kw-add-btn" onClick={addCustom}>Add</button>
              </div>
              {selectedItems.filter(kw => !catalog.flatMap(c => c.items).includes(kw)).length > 0 && (
                <div className="cpm__kw-chips" style={{ marginTop: '.35rem' }}>
                  {selectedItems.filter(kw => !catalog.flatMap(c => c.items).includes(kw)).map(kw => (
                    <span key={kw} className="cpm__kw-chip">
                      {kw}<button className="cpm__kw-chip-del" onClick={() => toggleKw(kw)}>✕</button>
                    </span>
                  ))}
                </div>
              )}
            </details>

            <div className="cpm__form-actions">
              <button className="cpm__save-btn" onClick={save} disabled={!form.name.trim()}>
                {creating ? '+ Create Profile' : '✓ Save Changes'}
              </button>
              <button className="cpm__cancel-btn" onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            </div>
          </div>
        )}

        {!isFormOpen && (
          <button className="cpm__new-btn" onClick={openCreate}>+ Create New Profile</button>
        )}
      </div>
    </div>,
    document.body
  );
}
